import type { QuickJSWASMModule } from 'quickjs-emscripten-core';
import type { CompiledExpression, ExpressionSandbox } from './ExpressionSandbox';

// Types only. Importing the class itself would put it, and the prelude it carries, in
// whichever chunk imports this module - which is the entry bundle, since LottieAsset needs
// the provenance check and the renderer wrapper synchronously. It is loaded below instead,
// beside the interpreter it cannot work without.
export type { CompiledExpression, ExpressionSandbox, FrameBindings } from './ExpressionSandbox';

let modulePromise: Promise<QuickJSWASMModule> | null = null;

// Lazy, and the single-file variant: the wasm is embedded, so it needs no bundler wasm
// handling and behaves the same in the browser, in nwjs and under the node test runner.
async function loadQuickJs() {
    if (!modulePromise) {
        modulePromise = (async () => {
            const [{ newQuickJSWASMModuleFromVariant }, variant] = await Promise.all([
                import('quickjs-emscripten-core'),
                import('@jitl/quickjs-singlefile-cjs-release-sync'),
            ]);
            const resolved = (variant as { default?: unknown }).default ?? variant;
            return newQuickJSWASMModuleFromVariant(
                resolved as Parameters<typeof newQuickJSWASMModuleFromVariant>[0],
            );
        })();
    }
    return modulePromise;
}

export async function createExpressionSandbox(): Promise<ExpressionSandbox> {
    // Fetched alongside the interpreter rather than after it, so being a separate chunk
    // costs no extra round trip.
    const [quickJs, { QuickJsExpressionSandbox }] = await Promise.all([
        loadQuickJs(),
        import('./ExpressionSandbox'),
    ]);
    return new QuickJsExpressionSandbox(quickJs);
}

let sharedSandbox: ExpressionSandbox | null = null;
let sharedSandboxPromise: Promise<ExpressionSandbox> | null = null;
let reportedSandboxLoadFailure = false;

/**
 * One asset exhausting the memory limit leaves the runtime unusable for every asset, so
 * drop it and let the next caller build a fresh one. Otherwise a single heavy lottie
 * disables expressions for the rest of the process.
 */
function discardSharedSandboxIfUnusable() {
    if (!sharedSandbox || sharedSandbox.usable) {
        return;
    }
    const dead = sharedSandbox;
    sharedSandbox = null;
    sharedSandboxPromise = null;
    dead.dispose();
}

/** The ready sandbox, or null while the interpreter is still loading. */
export function getSharedExpressionSandbox(): ExpressionSandbox | null {
    discardSharedSandboxIfUnusable();
    return sharedSandbox;
}

/** One interpreter is shared by every asset; the wasm module is expensive, contexts are not. */
export async function ensureSharedExpressionSandbox(): Promise<ExpressionSandbox> {
    discardSharedSandboxIfUnusable();
    if (!sharedSandboxPromise) {
        sharedSandboxPromise = createExpressionSandbox().then(
            (sandbox) => {
                sharedSandbox = sandbox;
                // A later outage is a new fact, so let it be reported again.
                reportedSandboxLoadFailure = false;
                return sandbox;
            },
            (error: unknown) => {
                // A rejected promise memoises as readily as a resolved one, and
                // discardSharedSandboxIfUnusable cannot reach this state - it keys off a
                // sandbox that loaded and then died, and this one never loaded. Both
                // caches have to go, or one transient chunk-load failure leaves every
                // untrusted lottie in the process rendering without its expressions.
                sharedSandboxPromise = null;
                modulePromise = null;
                throw error;
            },
        );
    }
    return sharedSandboxPromise;
}

/**
 * The interpreter is loaded once per process, so a failure to load is one fact however
 * many assets ask for it. Without this a chunk outage reports on every asset load and
 * again on every rebuild, which in the editor is every attribute change.
 */
export function reportSandboxLoadFailureOnce(report: () => void) {
    if (reportedSandboxLoadFailure) {
        return;
    }
    reportedSandboxLoadFailure = true;
    report();
}

/**
 * The shape lottie-web expects on `rendererSettings.expressionSandbox`. A dropped
 * expression falls back to baked keyframes, and `onDropped` is how we find out.
 *
 * lottie-web has no way to say a compiled expression is dead - its CompiledExpression is
 * evaluate() and nothing else, and the player holds one per property for the life of the
 * animation. So the caller owns them: everything compiled here is tracked, and
 * disposeCompiled releases it when the animation is torn down. Without that every rebuild
 * strands a handle in the shared runtime, and rebuilds happen on every attribute change.
 */
export function createRendererSandbox(
    sandbox: ExpressionSandbox,
    onDropped: (source: string, error: unknown) => void,
) {
    const compiled: CompiledExpression[] = [];
    return {
        compile: (source: string) => {
            const expression = sandbox.compile(source);
            compiled.push(expression);
            return expression;
        },
        onExpressionDropped: onDropped,
        disposeCompiled: () => {
            compiled.splice(0).forEach((expression) => {
                try {
                    expression.dispose();
                } catch {
                    // The runtime it belonged to is already gone, which is the outcome
                    // wanted here anyway.
                }
            });
        },
    };
}
